/**
 * JNP text reveal — word-by-word masked reveal for section headings.
 *
 * Splits eligible headings into per-word spans wrapped in an overflow-hidden
 * mask, then slides each word up from below with a staggered delay when the
 * heading scrolls into view. This is the signature "icon-amsterdam" heading
 * effect that pairs with the section slide-in.
 *
 * Tier 3 custom file — touches no upstream section. The visual styling lives
 * in jnp-text-reveal.css.
 *
 * Progressive enhancement & accessibility:
 *  - Disabled for prefers-reduced-motion and inside the theme editor.
 *  - The full heading text stays in the DOM (words + real spaces), so screen
 *    readers and text selection keep working.
 *  - Only plain-text headings are split; headings containing inline markup
 *    (links, <br>, <span>) are skipped so nothing breaks.
 *  - Opt out per heading with data-no-text-reveal.
 */
(function () {
  'use strict';

  // Sections whose headings should never be split.
  var SKIP_SECTION = /header|popup|announcement|marquee|scrolling-text|slideshow|breadcrumb/i;
  var MAX_WORDS = 20; // keep long body-like headings out of it

  function eligible(heading) {
    if (heading.hasAttribute('data-no-text-reveal')) return false;
    if (heading.children.length > 0) return false; // contains inline markup
    var text = heading.textContent.trim();
    if (!text) return false;
    if (text.split(/\s+/).length > MAX_WORDS) return false;
    var section = heading.closest('.shopify-section');
    if (section && SKIP_SECTION.test(section.id)) return false;
    // Skip headings already in view on load. Splitting them would briefly hide
    // the heading (often the LCP element) and cause a flash / CLS; leaving them
    // untouched lets them render normally.
    if (heading.getBoundingClientRect().top < window.innerHeight * 0.85) return false;
    return true;
  }

  function splitHeading(heading) {
    var words = heading.textContent.trim().split(/\s+/);
    heading.textContent = '';
    heading.classList.add('jnp-text-reveal');

    words.forEach(function (word, i) {
      var outer = document.createElement('span');
      outer.className = 'jnp-tr-word';
      var inner = document.createElement('span');
      inner.className = 'jnp-tr-word__in';
      inner.style.setProperty('--i', i);
      inner.textContent = word;
      outer.appendChild(inner);
      heading.appendChild(outer);
      // Real space between words so selection / screen readers stay natural.
      if (i < words.length - 1) {
        heading.appendChild(document.createTextNode(' '));
      }
    });
  }

  function init() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.Shopify && window.Shopify.designMode) return;

    var main = document.getElementById('MainContent');
    if (!main) return;

    var headings = Array.prototype.filter.call(
      main.querySelectorAll('h1, h2'),
      eligible
    );
    if (headings.length === 0) return;

    headings.forEach(splitHeading);

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px' }
    );

    headings.forEach(function (heading) {
      observer.observe(heading);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
